import { Link, useNavigate } from "react-router"
import { useTranslation } from "react-i18next"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { useCallback, useEffect } from "react"
import {
  Button,
  Input,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Checkbox,
} from "@repo/ui"
import { cn } from "@/lib/utils"
import { useLoginMutation } from "@/feature/auth/api"
import { PasswordInput } from "@/components/input/PasswordInput"
import { LogoExtended } from "@/routes/layouts/public-layout"

const schema = z.object({
  username: z.string().email(),
  password: z.string(),
  keep_active: z.boolean().optional(),
})

type FormValues = z.infer<typeof schema>

export const Login = () => {
  const { t } = useTranslation(`wizard`)
  const { t: commonT } = useTranslation(`common`)
  const navigate = useNavigate()
  const loginMutation = useLoginMutation()

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      username: ``,
      password: ``,
    },
    progressive: true,
  })

  const submitFn = useCallback(
    (data: FormValues) => {
      loginMutation.mutate(data)
    },
    [loginMutation]
  )

  useEffect(() => {
    if (loginMutation.error) {
      form.setError(`root`, {
        message: t(`login.screens.welcome.form.error`),
      })
      form.setError(`username`, {})
      form.setError(`password`, {})
    } else if (loginMutation.isSuccess) {
      if (loginMutation.data.user.status === `verified`) {
        navigate(`/wizard`)
      } else {
        navigate(`/`)
      }
    }
  }, [
    form,
    loginMutation.error,
    t,
    navigate,
    loginMutation.isSuccess,
    loginMutation.data?.user.status,
  ])
  return (
    <div className="bg-white w-full p-8 h-full flex flex-col items-center justify-between">
      <LogoExtended />
      <div className="max-w-md w-full ">
        <h1 className="text-4xl font-medium text-center">
          {t(`login.screens.welcome.title`)}
        </h1>
        <p className="text-muted-foreground max-w-sm mx-auto text-lg text-center">
          {t(`login.screens.welcome.description`)}
        </p>
        <Form {...form}>
          <form
            className="w-full flex flex-col gap-4 mt-10"
            onSubmit={form.handleSubmit(submitFn)}
          >
            {form.formState.errors.root ? (
              <FormMessage className="text-destructive text-center">
                {form.formState.errors.root.message}
              </FormMessage>
            ) : null}
            <FormField
              name="username"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      autoComplete="email"
                      placeholder="Email"
                      className={cn(
                        `border rounded`,
                        form.formState.errors.root
                          ? `border-destructive`
                          : `border-gray-300`
                      )}
                      {...field}
                      onChange={(e) => {
                        if (form.formState.errors.root) form.clearErrors(`root`)
                        field.onChange({
                          ...e,
                          target: {
                            ...e.target,
                            value: e.target.value.trim(),
                          },
                        })
                      }}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              name="password"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <PasswordInput
                      type="password"
                      placeholder="Password"
                      {...field}
                      onChange={(e) => {
                        if (form.formState.errors.root) form.clearErrors(`root`)
                        field.onChange(e)
                      }}
                      className={cn(
                        `border rounded`,
                        form.formState.errors.root
                          ? `border-destructive`
                          : `border-gray-300`
                      )}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="keep_active"
              render={({ field }) => (
                <div className="flex flex-row items-center justify-between">
                  <FormItem className="flex flex-row items-center gap-3">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel
                      className="p-0"
                      style={{
                        marginTop: `0px`,
                      }}
                    >
                      {t(`login.screens.welcome.form.remember`)}
                    </FormLabel>
                  </FormItem>
                  <Link to="/auth/forgot-password" className="primary-link">
                    <span className="text-primary text-sm">
                      {t(`login.screens.welcome.forgot_password.text`)}
                    </span>
                  </Link>
                </div>
              )}
            />
            <Button
              size="lg"
              type="submit"
              disabled={
                form.formState.isSubmitting ||
                !form.formState.isValid ||
                loginMutation.isPending
              }
            >
              {commonT(`button.login`)}
            </Button>
          </form>
        </Form>
      </div>
      <footer className="text-center text-sm">
        <div className="flex items-center gap-1 justify-center">
          <p className="text-muted-foreground">{commonT(`sign_up.text`)}</p>
          <Link to="/auth/signup" className="primary-link">
            {commonT(`sign_up.button`)}
          </Link>
        </div>
      </footer>
    </div>
  )
}
